import { useTranslation } from 'react-i18next';
import {
  ArrowsClockwise,
  Plus,
  FolderSimple,
  FileText,
  Archive,
  Tray,
} from '@phosphor-icons/react';
import { DocumentContentTab } from '../../constants';

export type ContentTab = DocumentContentTab;

interface FolderHeaderProps {
  onRefresh: () => void;
  onAdd: () => void;
  activeTab: ContentTab;
  onTabChange: (tab: ContentTab) => void;
  folderCount: number;
  documentCount: number;
  oldVersionsCount?: number;
  archivedDocumentsCount?: number;
  canAdd: boolean;
  showDocumentsTab?: boolean;
  showOldVersionsTab?: boolean;
  showArchivedTab?: boolean;
}

export const FolderHeader = ({
  onRefresh,
  onAdd,
  activeTab,
  onTabChange,
  folderCount,
  documentCount,
  oldVersionsCount = 0,
  archivedDocumentsCount = 0,
  canAdd,
  showDocumentsTab = true,
  showOldVersionsTab = false,
  showArchivedTab = false,
}: FolderHeaderProps) => {
  const { t } = useTranslation();

  const tabs: { id: ContentTab; label: string; count: number; icon: typeof FolderSimple }[] = [
    {
      id: DocumentContentTab.FOLDERS,
      label: t('documents.content_tabs.folders'),
      count: folderCount,
      icon: FolderSimple,
    },
    ...(showDocumentsTab
      ? [
          {
            id: DocumentContentTab.DOCUMENTS,
            label: t('documents.content_tabs.documents'),
            count: documentCount,
            icon: FileText,
          },
        ]
      : []),
    ...(showOldVersionsTab
      ? [
          {
            id: DocumentContentTab.OLD_VERSIONS,
            label: t('documents.content_tabs.old_versions'),
            count: oldVersionsCount,
            icon: Archive,
          },
        ]
      : []),
    ...(showArchivedTab
      ? [
          {
            id: DocumentContentTab.ARCHIVED,
            label: t('documents.content_tabs.archived'),
            count: archivedDocumentsCount,
            icon: Tray,
          },
        ]
      : []),
  ];

  const addButtonLabel =
    activeTab === DocumentContentTab.FOLDERS
      ? t('documents.add_subfolder')
      : t('documents.add_document');

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
        <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                <span>{tab.label}</span>
                <span
                  className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full ${
                    activeTab === tab.id ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-shrink-0 items-center gap-3 self-end lg:self-auto">
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 whitespace-nowrap px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowsClockwise size={18} />
            <span className="hidden sm:inline">{t('documents.refresh')}</span>
          </button>

          {canAdd && (
            <button
              onClick={onAdd}
              className="flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg shadow-sm hover:bg-opacity-90 transition"
            >
              <Plus size={18} />
              {addButtonLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
